import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { usePageContent } from "@/hooks/usePageContent";
import { useToast } from "@/hooks/use-toast";
import { Save, Loader2, BookOpen, Star, Heart, Sparkles, FileText, CheckCircle, Zap, Target, Users, Shield, Calendar, Clock, Leaf, Apple, Brain } from "lucide-react";

const ICON_MAP: Record<string, React.ElementType> = {
  BookOpen,
  Star,
  Heart,
  Sparkles,
  FileText,
  CheckCircle,
  Zap,
  Target,
  Users,
  Shield,
  Calendar,
  Clock,
  Leaf,
  Apple,
  Brain,
};

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{label}</label>
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, maxLength }: { value: string; onChange: (v: string) => void; placeholder?: string; maxLength?: number }) {
  return <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} maxLength={maxLength}
    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 transition-all" />;
}

function TextArea({ value, onChange, placeholder, rows = 3, maxLength }: { value: string; onChange: (v: string) => void; placeholder?: string; rows?: number; maxLength?: number }) {
  return <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows} maxLength={maxLength}
    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 transition-all resize-none" />;
}

function IconPickerInline({ value, onChange }: { value: string; onChange: (icon: string) => void }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const SelectedIcon = ICON_MAP[value] ?? BookOpen;

  const handleOpen = () => {
    if (btnRef.current) {
      setRect(btnRef.current.getBoundingClientRect());
    }
    setOpen(o => !o);
  };

  const handleSelect = (iconName: string) => {
    onChange(iconName);
    setOpen(false);
  };

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={handleOpen}
        className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 transition-all"
      >
        <SelectedIcon className="w-4 h-4 text-brand-primary" />
        <span className="text-gray-700">{value || 'BookOpen'}</span>
        <span className="text-gray-400 text-xs ml-1">▾</span>
      </button>

      {open && rect && createPortal(
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="fixed z-50 bg-white border border-gray-200 rounded-xl shadow-lg p-3 w-64"
            style={{ top: rect.bottom + 6, left: rect.left }}
          >
            <div className="grid grid-cols-5 gap-1.5">
              {Object.entries(ICON_MAP).map(([name, Icon]) => (
                <button
                  key={name}
                  type="button"
                  title={name}
                  onClick={() => handleSelect(name)}
                  className={`flex flex-col items-center justify-center p-2 rounded-lg transition-colors hover:bg-brand-primary/10 ${value === name ? 'bg-brand-primary/15 ring-1 ring-brand-primary/40' : ''}`}
                >
                  <Icon className="w-4 h-4 text-gray-700" />
                  <span className="text-[9px] text-gray-500 mt-0.5 leading-none truncate w-full text-center">{name}</span>
                </button>
              ))}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

export function BlogPageManager() {
  const { getText, get, upsert, loading } = usePageContent('blog_page');
  const { toast } = useToast();

  const [title, setTitle]           = useState('');
  const [description, setDesc]      = useState('');
  const [badges, setBadges]         = useState<string[]>([]);
  const [pillLabel, setPillLabel]   = useState('');
  const [pillIcon, setPillIcon]     = useState('BookOpen');
  const [saving, setSaving]         = useState(false);
  const [badgeDraft, setBadgeDraft] = useState('');

  useEffect(() => {
    if (loading) return;
    setTitle(getText('hero', 'title', 'Blog de Nutrição'));
    setDesc(getText('hero', 'description', 'Artigos, dicas e informações sobre nutrição e bem-estar para transformar sua relação com a comida.'));
    setBadges((get('hero', 'badges')?.content_value?.items as string[]) ?? ['Publicações semanais', 'Baseado em ciência', 'Conteúdo gratuito']);
    setPillLabel(getText('hero', 'pillLabel', 'Conteúdo Gratuito'));
    setPillIcon(getText('hero', 'pillIcon', 'BookOpen'));
  }, [loading]);

  const addBadge = () => { const t = badgeDraft.trim(); if (t) { setBadges(p => [...p, t]); setBadgeDraft(''); } };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        upsert('hero', 'title', { text: title }),
        upsert('hero', 'description', { text: description }),
        upsert('hero', 'badges', { items: badges }),
        upsert('hero', 'pillLabel', { text: pillLabel }),
        upsert('hero', 'pillIcon', { text: pillIcon }),
      ]);
      toast({ title: 'Blog salvo!' });
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 bg-white border border-gray-100 rounded-xl p-4">
      <div className="flex items-center gap-3 pb-3 border-b border-gray-100 mb-4">
        <div className="w-8 h-8 rounded-lg bg-brand-primary/8 flex items-center justify-center shrink-0">
          <BookOpen className="w-4 h-4 text-brand-primary" />
        </div>
        <div><h3 className="text-sm font-bold text-gray-800">Cabeçalho do Blog</h3><p className="text-xs text-gray-400">Seção verde no topo da página /blog</p></div>
      </div>

      <Field label="Pílula / Label" hint="Texto exibido na pílula acima do título">
        <TextInput value={pillLabel} onChange={setPillLabel} placeholder="Conteúdo Gratuito" maxLength={40} />
      </Field>

      <Field label="Pílula / Ícone" hint="Ícone exibido ao lado do label da pílula">
        <IconPickerInline value={pillIcon} onChange={setPillIcon} />
      </Field>

      <Field label="Título"><TextInput value={title} onChange={setTitle} placeholder="Blog de Nutrição" maxLength={100} /></Field>
      <Field label={`Descrição (${description.length}/400)`}><TextArea value={description} onChange={setDesc} rows={3} maxLength={400} /></Field>
      <Field label="Badges" hint="Chips exibidos abaixo da descrição">
        <div className="flex flex-wrap gap-1.5 mb-2">
          {badges.map((b, i) => (
            <span key={i} className="flex items-center gap-1 bg-brand-primary/8 text-brand-primary text-xs font-medium px-2.5 py-1 rounded-lg">
              {b}
              <button onClick={() => setBadges(p => p.filter((_, idx) => idx !== i))} className="text-brand-primary/50 hover:text-brand-primary ml-0.5 text-[10px]">✕</button>
            </span>
          ))}
        </div>
        {badges.length < 5 && (
          <div className="flex gap-2">
            <input type="text" value={badgeDraft} onChange={e => setBadgeDraft(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addBadge(); } }} placeholder="Ex: Publicações semanais" maxLength={40}
              className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 transition-all" />
            <button onClick={addBadge} className="bg-gray-100 hover:bg-brand-primary/10 text-gray-600 hover:text-brand-primary text-xs font-medium px-3 py-1.5 rounded-lg transition-colors">Adicionar</button>
          </div>
        )}
      </Field>

      <button onClick={handleSave} disabled={saving}
        className="flex items-center gap-2 bg-brand-primary hover:bg-brand-primary/90 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Salvar
      </button>
    </div>
  );
}
